// Execution library - task loading, completion, reassignment, deletion operations
// UPDATED: fetchAndAssembleTasks now accepts startDate/endDate range; getAllTeamTasksForDateRange and getTeamTasksForDateRange delegate to it for proper recurring task expansion in list view
// CHANGE: Removed all debug console.log/warn/error statements for production
// UPDATED: Removed ItemCompletionUpdate, updateTaskItemCompletion, completedItems/progressPercentage from summary
// UPDATED: Main SELECT queries now read template metadata from snapshot columns instead of JOIN to task_templates
// FIX: insertKpiEntries now uses ignoreDuplicates:true so operators (no UPDATE policy) can insert without RLS failure
// SECURITY: Removed rawSnippet from catch block logs to prevent user data exposure in error logs
// SECURITY: updateOccurrenceItemStates now uses server-side RPC to atomically merge item state, eliminating read-modify-write race condition
// UPDATED: This ensures completed tasks are not affected by template modifications
// UPDATED: completeTask now freezes template metadata + item structure into the occurrence record on completion
// UPDATED: createVirtualOccurrence uses frozen metadata for completed occurrences
// UPDATED: fetchAndAssembleTasks uses frozenItems from notes for completed occurrences
// FIXED: insertKpiEntries now checks upsert result for errors and logs them instead of silently dropping
// UPDATED: KPI lifecycle sync - kpi_entries are now deleted when a completed task/occurrence is reverted,
//          deleted, or cancelled; and upserted when an admin edits a numeric field on a completed task
// FIXED: fetchAndAssembleTasks now patches assigned_to_user on virtual occurrences that were reassigned
//        to a different operator so the display name matches the actual assignee
// REFACTORED: Extracted hydrateReassignedUsers helper; reused in getAssignedTaskById for single-task loads
// UPDATED: fetchAndAssembleTasks, getAllTeamTasksForDateRange, getTeamTasksForDateRange now accept an optional
//          AbortSignal and forward it into all Supabase queries so network requests cancel on abort
// FIX: getAssignedTaskById now includes assigned_to_user join on both virtual and direct task paths
// UPDATED: updateOccurrenceItemStates now preserves frozenItems (and all notes fields) when updating itemStates
// FIXED: updateTaskStatus completion path now fully mirrors completeTask: template_* snapshot columns are written to
//        the occurrence, every item gets is_completed:true in itemStates, and KPI entries are inserted; the direct-task
//        path now also marks assigned_task_items completed and inserts KPI entries; Promise.all error checking added
// FIXED: updateTaskStatus revert paths now reset is_completed flags: virtual occurrence revert resets itemStates in
//        notes JSON to is_completed:false preserving text_value; direct-task revert resets assigned_task_items rows
// FIXED: updateTaskStatus adds error guards for parentTaskResult and taskInfoResult before any write
// FIXED: direct-task revert now resets assigned_task_items before updating assigned_tasks status to avoid window where
//        task is not_started but items are still marked completed
// FIXED: itemStates in occurrence notes now keyed by task_item_id (stable template reference) instead of
//        assigned_task_items.id (generated UUID that changes on regeneration); frozenItems now also stores
//        task_item_id; read paths fall back to item.id for backward compatibility with pre-migration data;
//        updateTaskItemTextInput/AsAdmin resolve task_item_id via a DB lookup before calling the RPC

import { supabase } from './supabase';
import { formatDate, parseDate } from './dateUtils';
import { getTaskOccurrences, calculateOccurrences, mergeWithExceptions, batchLoadOccurrenceExceptions, type CalculatedOccurrence } from './occurrences';
import type { RecurrenceRule, AssignedTaskOccurrence, AssignedTask } from './supabase';
import { getCurrentDate, isToday, logPermissionDenial } from './taskPermissions';
import type { TaskStatus } from './statusUtils';
import { addBreadcrumb } from './sentry';

interface TaskTemplate {
  id: string;
  name: string;
  description: string | null;
  estimated_duration_minutes: number;
  service_id: string | null;
}

interface TaskSite {
  id: string;
  name: string;
}

interface TaskUser {
  id: string;
  full_name: string;
  username: string;
}

interface RawTaskWithJoins {
  id: string;
  assigned_to_user_id: string;
  site_id: string;
  template_id: string;
  assigned_date: string;
  scheduled_time: string | null;
  status: TaskStatus;
  completed_at: string | null;
  is_recurring: boolean;
  duration_override: number | null;
  recurrence_end_date: string | null;
  recurrence_start_date: string | null;
  recurrence_rule_id: string | null;
  recurrence_pattern: string | null;
  validated_at: string | null;
  validated_by: string | null;
  parent_task_id: string | null;
  reassignment_from_date: string | null;
  reassignment_user_id: string | null;
  reassignment_scheduled_time: string | null;
  template_name: string | null;
  template_description: string | null;
  template_estimated_duration_minutes: number | null;
  template_service_id: string | null;
  template?: TaskTemplate | null;
  site: TaskSite | null;
  assigned_to_user: TaskUser | null;
  recurrence?: RecurrenceRule | RecurrenceRule[];
}

interface TaskCheckResult {
  id: string;
  status: TaskStatus;
  validated_at: string | null;
  site_id: string;
  assigned_to_user_id: string;
  assigned_date: string;
  is_recurring: boolean;
  template?: { name: string } | null;
  site?: { id: string; name?: string } | null;
  assigned_to_user?: { id: string; full_name: string } | null;
}

interface OccurrenceReassignUpdate {
  assigned_to_user_id_override: string;
  scheduled_time_override?: string;
}

interface DirectTaskReassignUpdate {
  assigned_to_user_id: string;
  scheduled_time?: string;
}

interface ReassignmentRuleUpdate {
  reassignment_from_date: string;
  reassignment_user_id: string;
  reassignment_scheduled_time: string | null;
}

interface TaskStatusUpdate {
  status: TaskStatus;
  completed_at: string | null;
}

// Canonical type for assigned tasks with joined data (used by both execution and templates modules)
export interface AssignedTaskWithDetails {
  id: string;
  assigned_to_user_id: string;
  site_id: string;
  template_id: string;
  assigned_date: string;
  scheduled_time: string | null;
  status: 'not_started' | 'completed' | 'overdue';
  completed_at: string | null;
  is_recurring: boolean;
  recurrence_pattern: string | null;
  duration_override_minutes: number | null;
  template: {
    id: string;
    name: string;
    description: string | null;
    estimated_duration_minutes: number;
    service_id: string | null;
  };
  template_service_id?: string | null;
  site: {
    id: string;
    name: string;
  };
  assigned_to_user?: {
    id: string;
    full_name: string;
    username: string;
  };
  user?: {
    id: string;
    username: string;
    full_name: string;
  };
  items: AssignedTaskItem[];
  isVirtualOccurrence?: boolean;
  parentTaskId?: string;
  occurrenceDate?: string;
  occurrenceData?: AssignedTaskOccurrence;
}

export interface AssignedTaskItem {
  id: string;
  assigned_task_id: string;
  task_item_id: string;
  title: string;
  description: string | null;
  order_index: number;
  is_completed: boolean;
  completed_at: string | null;
  requires_text_input: boolean;
  is_text_input_required: boolean;
  text_value: string | null;
  text_input_placeholder?: string | null;
  // KPI system v1
  text_input_type?: 'text' | 'number';
  kpi_definition_id?: string | null;
}

export interface TaskExecutionSummary {
  totalItems: number;
  hasRequiredFields: boolean;
  missingRequiredFields: number;
  canComplete: boolean;
}

async function logTaskActivity(params: {
  taskId: string;
  occurrenceDate?: string | null;
  actionType: 'completion' | 'status_change' | 'text_input_update' | 'reassignment' | 'reschedule' | 'overdue_auto' | 'deletion';
  actorId?: string | null;
  actorName?: string | null;
  actorType?: 'user' | 'system';
  oldValue?: Record<string, unknown> | null;
  newValue?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}): Promise<void> {
  try {
    let actorId = params.actorId ?? null;
    if (!actorId) {
      const { data: { session } } = await supabase.auth.getSession();
      actorId = session?.user?.id ?? null;
    }

    let actorName = params.actorName ?? null;
    if (actorId && !actorName) {
      const { data: userData } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', actorId)
        .maybeSingle();
      actorName = userData?.full_name ?? null;
    }

    await supabase.from('task_activity_logs').insert({
      task_id: params.taskId,
      occurrence_date: params.occurrenceDate ?? null,
      action_type: params.actionType,
      actor_id: actorId,
      actor_name: actorName,
      actor_type: params.actorType ?? 'user',
      old_value: params.oldValue ?? null,
      new_value: params.newValue ?? null,
      metadata: params.metadata ?? null,
    });
  } catch {
    // Logging failures are non-fatal - never block the main operation
  }
}

/**
 * OPTIMIZATION: Batch load all items for multiple tasks in a single query
 * Reduces N queries to 1 query using IN clause
 */
async function batchLoadTaskItems(taskIds: string[]): Promise<Map<string, AssignedTaskItem[]>> {
  if (taskIds.length === 0) {
    return new Map();
  }

  const { data: allItems, error } = await supabase
    .from('assigned_task_items')
    .select('*')
    .in('assigned_task_id', taskIds)
    .order('assigned_task_id, order_index', { ascending: true });

  if (error) {
    throw error;
  }

  // Group items by task_id
  const itemsMap = new Map<string, AssignedTaskItem[]>();
  (allItems || []).forEach(item => {
    const existing = itemsMap.get(item.assigned_task_id) || [];
    existing.push(item);
    itemsMap.set(item.assigned_task_id, existing);
  });

  return itemsMap;
}

/**
 * Helper: Create a virtual occurrence task from a recurring task and calculated occurrence
 */
function createVirtualOccurrence(
  parentTask: RawTaskWithJoins,
  occurrence: CalculatedOccurrence,
  occurrenceRecord: AssignedTaskOccurrence | null
): AssignedTaskWithDetails {
  const virtualId = `${parentTask.id}_${occurrence.date}`;

  const isCompleted = occurrenceRecord?.status === 'completed';
  const hasFrozenMetadata = isCompleted && occurrence.frozenTemplateName !== null;

  const templateName = hasFrozenMetadata
    ? (occurrence.frozenTemplateName ?? '')
    : (parentTask.template_name || '');
  const templateDescription = hasFrozenMetadata
    ? occurrence.frozenTemplateDescription
    : (parentTask.template_description || null);
  const templateDuration = hasFrozenMetadata
    ? (occurrence.frozenTemplateEstimatedDurationMinutes ?? 0)
    : (parentTask.template_estimated_duration_minutes || 0);
  const templateServiceId = hasFrozenMetadata
    ? occurrence.frozenTemplateServiceId
    : (parentTask.template_service_id || null);

  return {
    id: virtualId,
    assigned_to_user_id: occurrence.assignedUserId,
    site_id: parentTask.site_id,
    template_id: parentTask.template_id,
    assigned_date: occurrence.date,
    scheduled_time: occurrence.scheduledTime || parentTask.scheduled_time,
    status: occurrenceRecord?.status !== null && occurrenceRecord?.status !== undefined
      ? occurrenceRecord.status
      : occurrence.status,
    completed_at: null,
    is_recurring: true,
    recurrence_pattern: null,
    duration_override_minutes: occurrence.duration || parentTask.duration_override,
    template_service_id: templateServiceId,
    template: {
      id: parentTask.template_id,
      name: templateName,
      description: templateDescription,
      estimated_duration_minutes: templateDuration,
      service_id: templateServiceId
    },
    site: parentTask.site as AssignedTaskWithDetails['site'],
    assigned_to_user: parentTask.assigned_to_user,
    items: [],
    isVirtualOccurrence: true,
    parentTaskId: parentTask.id,
    occurrenceDate: occurrence.date,
    occurrenceData: occurrenceRecord || undefined
  };
}

/**
 * Helper: Get or load items for a virtual occurrence
 * Items are duplicated from the parent task's template items
 */
async function getItemsForOccurrence(
  parentTaskId: string,
  occurrenceData: AssignedTaskOccurrence | null
): Promise<AssignedTaskItem[]> {
  // Get the template items from the parent task
  const { data: parentItems, error } = await supabase
    .from('assigned_task_items')
    .select('*')
    .eq('assigned_task_id', parentTaskId)
    .order('order_index', { ascending: true });

  if (error) {
    throw error;
  }

  if (!parentItems || parentItems.length === 0) {
    return [];
  }

  let itemStates: Record<string, { is_completed: boolean; text_value?: string }> = {};
  let frozenItemsFromNotes: Array<Omit<AssignedTaskItem, 'assigned_task_id'>> | null = null;

  if (occurrenceData?.notes) {
    try {
      const parsed = JSON.parse(occurrenceData.notes);
      if (parsed.itemStates) {
        itemStates = parsed.itemStates;
      }
      if (parsed.frozenItems && Array.isArray(parsed.frozenItems)) {
        frozenItemsFromNotes = parsed.frozenItems;
      }
    } catch (e) {
      // Notes is not JSON, ignore
    }
  }

  const isCompleted = occurrenceData?.status === 'completed';
  const sourceItems = (isCompleted && frozenItemsFromNotes !== null)
    ? frozenItemsFromNotes.map(fi => ({
        ...fi,
        assigned_task_id: parentTaskId,
        task_item_id: fi.task_item_id || fi.id
      }) as AssignedTaskItem)
    : parentItems;

  return sourceItems.map(item => {
    const state = itemStates[item.task_item_id] || itemStates[item.id];
    return {
      ...item,
      id: `${item.id}_virtual`,
      is_completed: state?.is_completed || false,
      text_value: state?.text_value || item.text_value,
      completed_at: state?.is_completed ? new Date().toISOString() : null
    };
  });
}

interface SiteFilterUserSites {
  type: 'userSites';
  userId: string;
}

interface SiteFilterSingle {
  type: 'singleSite';
  siteId: string;
}

interface AccessCheck {
  userId: string;
}

interface TaskFetchFilters {
  siteFilter: SiteFilterUserSites | SiteFilterSingle;
  accessCheck?: AccessCheck;
  userFilter?: { userId: string };
}

const DIRECT_TASK_SELECT = `
  id,
  assigned_to_user_id,
  site_id,
  template_id,
  assigned_date,
  scheduled_time,
  status,
  completed_at,
  is_recurring,
  recurrence_pattern,
  duration_override,
  template_name,
  template_description,
  template_estimated_duration_minutes,
  template_service_id,
  site:sites(id, name),
  assigned_to_user:users!assigned_tasks_assigned_to_user_id_fkey(id, full_name, username)
`;

const RECURRING_TASK_SELECT = `
  id,
  assigned_to_user_id,
  site_id,
  template_id,
  assigned_date,
  scheduled_time,
  status,
  completed_at,
  is_recurring,
  recurrence_rule_id,
  recurrence_start_date,
  recurrence_end_date,
  duration_override,
  reassignment_from_date,
  reassignment_user_id,
  reassignment_scheduled_time,
  template_name,
  template_description,
  template_estimated_duration_minutes,
  template_service_id,
  site:sites(id, name),
  assigned_to_user:users!assigned_tasks_assigned_to_user_id_fkey(id, full_name, username),
  recurrence:recurrence_rules!assigned_tasks_recurrence_rule_id_fkey(*)
`;

async function hydrateReassignedUsers(
  virtualOccurrences: AssignedTaskWithDetails[]
): Promise<void> {
  const overrideUserIds = new Set<string>();
  virtualOccurrences.forEach(vt => {
    if (vt.assigned_to_user_id && vt.assigned_to_user_id !== vt.assigned_to_user?.id) {
      overrideUserIds.add(vt.assigned_to_user_id);
    }
  });

  if (overrideUserIds.size === 0) return;

  const { data: overrideUsers } = await supabase
    .from('users')
    .select('id, full_name, username')
    .in('id', Array.from(overrideUserIds));

  const overrideUsersById = new Map<string, TaskUser>();
  (overrideUsers || []).forEach((u: TaskUser) => overrideUsersById.set(u.id, u));

  virtualOccurrences.forEach(vt => {
    if (vt.assigned_to_user_id && vt.assigned_to_user_id !== vt.assigned_to_user?.id) {
      const override = overrideUsersById.get(vt.assigned_to_user_id);
      if (override) {
        vt.assigned_to_user = override;
      }
    }
  });
}

async function fetchAndAssembleTasks(
  startDate: string,
  endDate: string,
  filters: TaskFetchFilters,
  signal?: AbortSignal
): Promise<AssignedTaskWithDetails[]> {
  let siteIds: string[] = [];

  if (filters.siteFilter.type === 'userSites') {
    let userSitesQuery = supabase
      .from('user_sites')
      .select('site_id')
      .eq('user_id', filters.siteFilter.userId);
    if (signal) userSitesQuery = userSitesQuery.abortSignal(signal);
    const { data: userSites, error: sitesError } = await userSitesQuery;

    if (sitesError) {
      throw sitesError;
    }

    if (!userSites || userSites.length === 0) {
      return [];
    }

    siteIds = userSites.map(s => s.site_id);
  } else {
    siteIds = [filters.siteFilter.siteId];
  }

  if (filters.accessCheck && filters.siteFilter.type === 'singleSite') {
    let accessSitesQuery = supabase
      .from('user_sites')
      .select('site_id')
      .eq('user_id', filters.accessCheck.userId);
    if (signal) accessSitesQuery = accessSitesQuery.abortSignal(signal);
    const { data: accessSites, error: accessError } = await accessSitesQuery;

    if (accessError) {
      throw accessError;
    }

    const accessSiteIds = accessSites?.map(s => s.site_id) || [];
    if (!accessSiteIds.includes(filters.siteFilter.siteId)) {
      throw new Error('Manager does not have access to this site');
    }
  }

  const useSingleSite = siteIds.length === 1;
  const siteFilterValue = useSingleSite ? siteIds[0] : siteIds;

  let directQuery = supabase
    .from('assigned_tasks')
    .select(DIRECT_TASK_SELECT)
    .gte('assigned_date', startDate)
    .lte('assigned_date', endDate)
    .eq('is_recurring', false);

  if (useSingleSite) {
    directQuery = directQuery.eq('site_id', siteFilterValue as string);
  } else {
    directQuery = directQuery.in('site_id', siteFilterValue as string[]);
  }

  if (filters.userFilter) {
    directQuery = directQuery.eq('assigned_to_user_id', filters.userFilter.userId);
  }

  let recurringQuery = supabase
    .from('assigned_tasks')
    .select(RECURRING_TASK_SELECT)
    .eq('is_recurring', true)
    .not('recurrence_rule_id', 'is', null)
    .lte('recurrence_start_date', endDate);

  if (useSingleSite) {
    recurringQuery = recurringQuery.eq('site_id', siteFilterValue as string);
  } else {
    recurringQuery = recurringQuery.in('site_id', siteFilterValue as string[]);
  }

  if (signal) {
    directQuery = directQuery.abortSignal(signal);
    recurringQuery = recurringQuery.abortSignal(signal);
  }

  const [directTasksResult, recurringTasksResult] = await Promise.all([
    directQuery,
    recurringQuery
  ]);

  const { data: directTasks, error: directError } = directTasksResult;
  const { data: recurringTasks, error: recurringError } = recurringTasksResult;

  if (directError) {
    throw directError;
  }

  if (recurringError) {
    throw recurringError;
  }

  const typedDirectTasks = (directTasks || []) as RawTaskWithJoins[];
  const typedRecurringTasks = (recurringTasks || []) as RawTaskWithJoins[];

  const allTaskIds = [
    ...typedDirectTasks.map(t => t.id),
    ...typedRecurringTasks.map(t => t.id)
  ];

  const itemsMap = await batchLoadTaskItems(allTaskIds);

  const directTasksWithItems = typedDirectTasks.map(task => ({
    ...task,
    items: itemsMap.get(task.id) || [],
    duration_override_minutes: task.duration_override,
    template_service_id: task.template_service_id || null,
    template: {
      id: task.template_id,
      name: task.template_name || '',
      description: task.template_description || null,
      estimated_duration_minutes: task.template_estimated_duration_minutes || 0,
      service_id: task.template_service_id || null
    }
  }));

  const recurringTaskIds = typedRecurringTasks.map(t => t.id);
  const rangeStart = parseDate(startDate);
  const rangeEnd = parseDate(endDate);
  const exceptionsMap = await batchLoadOccurrenceExceptions(recurringTaskIds, rangeStart, rangeEnd);

  const virtualOccurrences: AssignedTaskWithDetails[] = [];

  for (const task of typedRecurringTasks) {
    const rule = Array.isArray(task.recurrence) ? task.recurrence[0] : task.recurrence;

    if (!rule) {
      continue;
    }

    if (task.recurrence_end_date && startDate > task.recurrence_end_date) {
      continue;
    }

    const preloadedExceptions = exceptionsMap.get(task.id) || [];
    const calculated = calculateOccurrences(task, rule, { startDate: rangeStart, endDate: rangeEnd });
    const occurrences = mergeWithExceptions(calculated, preloadedExceptions, task);

    const templateItems = itemsMap.get(task.id) || [];

    for (const occurrence of occurrences) {
      if (filters.userFilter && occurrence.assignedUserId !== filters.userFilter.userId) {
        continue;
      }

      let itemStates: Record<string, { is_completed: boolean; text_value?: string }> = {};
      let frozenItemsFromNotes: Array<Omit<AssignedTaskItem, 'assigned_task_id'>> | null = null;

      if (occurrence.notes) {
        try {
          const parsed = JSON.parse(occurrence.notes);
          if (parsed.itemStates) {
            itemStates = parsed.itemStates;
          }
          if (parsed.frozenItems && Array.isArray(parsed.frozenItems)) {
            frozenItemsFromNotes = parsed.frozenItems;
          }
        } catch (e) {
          // Notes is not JSON, ignore
        }
      }

      const isCompletedOccurrence = occurrence.status === 'completed';
      const sourceItems = (isCompletedOccurrence && frozenItemsFromNotes !== null)
        ? frozenItemsFromNotes.map(fi => ({
            ...fi,
            assigned_task_id: task.id,
            task_item_id: fi.task_item_id || fi.id
          }) as AssignedTaskItem)
        : templateItems;

      const items = sourceItems.map(item => {
        const state = itemStates[item.task_item_id] || itemStates[item.id];
        return {
          ...item,
          id: `${item.id}_virtual`,
          is_completed: state?.is_completed || false,
          text_value: state?.text_value || item.text_value,
          completed_at: state?.is_completed ? new Date().toISOString() : null
        };
      });

      const occurrenceRecord = occurrence.occurrenceId ? {
        id: occurrence.occurrenceId,
        assigned_task_id: task.id,
        occurrence_date: occurrence.date,
        status: occurrence.status,
        is_cancelled: occurrence.isCancelled,
        scheduled_time_override: occurrence.scheduledTime,
        duration_override: occurrence.duration,
        assigned_to_user_id_override: null,
        notes: occurrence.notes,
        template_name: occurrence.frozenTemplateName,
        template_description: occurrence.frozenTemplateDescription,
        template_estimated_duration_minutes: occurrence.frozenTemplateEstimatedDurationMinutes,
        template_service_id: occurrence.frozenTemplateServiceId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as AssignedTaskOccurrence : null;

      const virtualTask = createVirtualOccurrence(task, occurrence, occurrenceRecord);
      virtualTask.items = items;
      virtualTask.duration_override_minutes = occurrence.duration || task.duration_override;

      virtualOccurrences.push(virtualTask);
    }
  }

  await hydrateReassignedUsers(virtualOccurrences);

  const todayStr = formatDate(new Date());
  const allTasks = [...directTasksWithItems, ...virtualOccurrences];
  return allTasks.map(task => ({
    ...task,
    status: (task.status === 'not_started' && task.assigned_date < todayStr)
      ? 'overdue' as const
      : task.status
  }));
}

/**
 * Get all assigned tasks for a specific user on a specific date
 * Uses client-side recurrence calculation
 */
export async function getAssignedTasksForDate(
  userId: string,
  date: string
): Promise<AssignedTaskWithDetails[]> {
  return fetchAndAssembleTasks(date, date, {
    siteFilter: { type: 'userSites', userId },
    userFilter: { userId }
  });
}

/**
 * Get a single assigned task with all its items
 * UPDATED: Supports virtual occurrence IDs (format: parentTaskId_date)
 */
export async function getAssignedTaskById(
  taskId: string
): Promise<AssignedTaskWithDetails | null> {
  // Check if this is a virtual occurrence ID (contains underscore with date format)
  const isVirtualId = taskId.includes('_') && taskId.split('_')[1]?.match(/^\d{4}-\d{2}-\d{2}$/);

  if (isVirtualId) {
    const [parentTaskId, occurrenceDate] = taskId.split('_');

    // Load the parent recurring task
    const { data: parentTask, error: parentError } = await supabase
      .from('assigned_tasks')
      .select(`
        id,
        assigned_to_user_id,
        site_id,
        template_id,
        assigned_date,
        scheduled_time,
        status,
        completed_at,
        is_recurring,
        recurrence_rule_id,
        recurrence_start_date,
        recurrence_end_date,
        duration_override,
        reassignment_from_date,
        reassignment_user_id,
        reassignment_scheduled_time,
        template_name,
        template_description,
        template_estimated_duration_minutes,
        template_service_id,
        site:sites(id, name),
        assigned_to_user:users!assigned_tasks_assigned_to_user_id_fkey(id, full_name, username),
        recurrence:recurrence_rules!assigned_tasks_recurrence_rule_id_fkey(*)
      `)
      .eq('id', parentTaskId)
      .maybeSingle();

    if (parentError) {
      throw parentError;
    }

    if (!parentTask) {
      return null;
    }

    // Load occurrence data if it exists
    const { data: occurrenceData, error: occError } = await supabase
      .from('assigned_task_occurrences')
      .select('*')
      .eq('assigned_task_id', parentTaskId)
      .eq('occurrence_date', occurrenceDate)
      .maybeSingle();

    if (occError) {
      throw occError;
    }

    // Load items for this occurrence
    const items = await getItemsForOccurrence(parentTaskId, occurrenceData);

    // Extract recurrence rule
    const rule = Array.isArray(parentTask.recurrence) ? parentTask.recurrence[0] : parentTask.recurrence;

    if (!rule) {
      return null;
    }

    // Calculate the occurrence for this date
    const targetDate = parseDate(occurrenceDate);
    const occurrences = await getTaskOccurrences(
      parentTask,
      rule,
      {
        startDate: targetDate,
        endDate: targetDate
      }
    );

    if (occurrences.length === 0) {
      return null;
    }

    const occurrence = occurrences[0];

    // Create virtual task
    const virtualTask = createVirtualOccurrence(parentTask, occurrence, occurrenceData);
    virtualTask.items = items;
    virtualTask.duration_override_minutes = occurrence.duration || parentTask.duration_override;

    await hydrateReassignedUsers([virtualTask]);

    const todayForVirtual = formatDate(new Date());
    if (virtualTask.status === 'not_started' && virtualTask.assigned_date < todayForVirtual) {
      virtualTask.status = 'overdue';
    }

    return virtualTask;
  }

  // Regular direct task loading
  const { data: task, error: taskError } = await supabase
    .from('assigned_tasks')
    .select(`
      id,
      assigned_to_user_id,
      site_id,
      template_id,
      assigned_date,
      scheduled_time,
      status,
      completed_at,
      is_recurring,
      recurrence_pattern,
      duration_override,
      template_name,
      template_description,
      template_estimated_duration_minutes,
      template_service_id,
      site:sites(id, name),
      assigned_to_user:users!assigned_tasks_assigned_to_user_id_fkey(id, full_name, username)
    `)
    .eq('id', taskId)
    .maybeSingle();

  if (taskError) {
    throw taskError;
  }

  if (!task) return null;

  const { data: items, error: itemsError } = await supabase
    .from('assigned_task_items')
    .select('*')
    .eq('assigned_task_id', taskId)
    .order('order_index', { ascending: true });

  if (itemsError) {
    throw itemsError;
  }

  const todayDirect = formatDate(new Date());
  const directStatus = (task.status === 'not_started' && task.assigned_date < todayDirect)
    ? 'overdue' as const
    : task.status;

  return {
    ...task,
    status: directStatus,
    items: items || [],
    duration_override_minutes: task.duration_override,
    template_service_id: task.template_service_id || null,
    template: {
      id: task.template_id,
      name: task.template_name || '',
      description: task.template_description || null,
      estimated_duration_minutes: task.template_estimated_duration_minutes || 0,
      service_id: task.template_service_id || null
    }
  } as AssignedTaskWithDetails;
}

/**
 * Calculate execution summary for a task
 * UPDATED: Validates required text fields
 */
export function calculateExecutionSummary(
  items: AssignedTaskItem[]
): TaskExecutionSummary {
  const totalItems = items.length;

  const requiredFields = items.filter(item => item.is_text_input_required);
  const hasRequiredFields = requiredFields.length > 0;
  const missingRequiredFields = requiredFields.filter(item => {
    if (!item.text_value || item.text_value.trim() === '') return true;
    if (item.text_input_type === 'number') {
      const parsed = parseFloat(item.text_value.trim());
      return isNaN(parsed);
    }
    return false;
  }).length;

  const canComplete = missingRequiredFields === 0;

  return {
    totalItems,
    hasRequiredFields,
    missingRequiredFields,
    canComplete
  };
}

/**
 * Helper: Get or create an occurrence record in the database
 */
async function getOrCreateOccurrence(
  parentTaskId: string,
  occurrenceDate: string
): Promise<AssignedTaskOccurrence> {
  // Check if occurrence record already exists
  const { data: existing, error: fetchError } = await supabase
    .from('assigned_task_occurrences')
    .select('*')
    .eq('assigned_task_id', parentTaskId)
    .eq('occurrence_date', occurrenceDate)
    .maybeSingle();

  if (fetchError) {
    throw fetchError;
  }

  if (existing) {
    return existing;
  }

  // Create new occurrence record
  const { data: newOccurrence, error: createError } = await supabase
    .from('assigned_task_occurrences')
    .insert({
      assigned_task_id: parentTaskId,
      occurrence_date: occurrenceDate,
      status: 'not_started',
      is_cancelled: false,
      scheduled_time_override: null,
      duration_override: null,
      assigned_to_user_id_override: null,
      notes: JSON.stringify({ itemStates: {} })
    })
    .select()
    .single();

  if (createError) {
    // Handle race condition: if another concurrent request already created this occurrence,
    // the unique constraint will fail with error code 23505
    if (createError.code === '23505') {
      // Retry fetch - the other concurrent request created it
      const { data: retryFetch, error: retryError } = await supabase
        .from('assigned_task_occurrences')
        .select('*')
        .eq('assigned_task_id', parentTaskId)
        .eq('occurrence_date', occurrenceDate)
        .single();

      if (retryError || !retryFetch) {
        throw retryError || new Error('Failed to fetch occurrence after race condition');
      }

      return retryFetch;
    }

    // For other errors, throw as before
    throw createError;
  }

  return newOccurrence;
}

/**
 * Helper: Update item states in an occurrence's notes field
 * UPDATED: Preserves all existing notes fields (frozenItems, etc.) when updating itemStates
 */
async function updateOccurrenceItemStates(
  occurrenceId: string,
  itemId: string,
  updates: { text_value?: string }
): Promise<void> {
  const { error } = await supabase.rpc('update_occurrence_item_state', {
    p_occurrence_id: occurrenceId,
    p_item_id: itemId,
    p_updates: updates,
  });

  if (error) {
    throw error;
  }
}

/**
 * Validate if a task date can be modified (temporal access control)
 * Returns true if date is today, false otherwise
 */
function validateTaskDateForModification(taskDate: string): boolean {
  return isToday(taskDate);
}

/**
 * Helper to validate and throw error if task date cannot be modified
 */
function assertTaskCanBeModified(taskDate: string, operation: string): void {
  if (!validateTaskDateForModification(taskDate)) {
    throw new Error(`Cette mission ne peut pas être modifiée. Seules les missions du jour actuel sont modifiables.`);
  }
}

/**
 * Update task item text input value
 * UPDATED: Handles both direct tasks and virtual occurrences
 * UPDATED: Added temporal access validation
 */
export async function updateTaskItemTextInput(
  itemId: string,
  textValue: string,
  taskContext?: { isVirtual: boolean; parentTaskId?: string; occurrenceDate?: string },
  logContext?: { taskId?: string; fieldTitle?: string }
): Promise<void> {
  // Temporal access control: Validate task date
  if (taskContext?.isVirtual && taskContext.occurrenceDate) {
    assertTaskCanBeModified(taskContext.occurrenceDate, 'updateTaskItemTextInput');
  } else {
    // For direct tasks, fetch the task's assigned_date
    const { data: item } = await supabase
      .from('assigned_task_items')
      .select('assigned_task_id')
      .eq('id', itemId.replace('_virtual', ''))
      .single();

    if (item) {
      const { data: task } = await supabase
        .from('assigned_tasks')
        .select('assigned_date')
        .eq('id', item.assigned_task_id)
        .single();

      if (task) {
        assertTaskCanBeModified(task.assigned_date, 'updateTaskItemTextInput');
      }
    }
  }

  const realItemId = itemId.replace('_virtual', '');

  if (taskContext?.isVirtual && taskContext.parentTaskId && taskContext.occurrenceDate) {
    const occurrence = await getOrCreateOccurrence(
      taskContext.parentTaskId,
      taskContext.occurrenceDate
    );

    const { data: itemRow } = await supabase
      .from('assigned_task_items')
      .select('task_item_id')
      .eq('id', realItemId)
      .maybeSingle();
    const stableKey = itemRow?.task_item_id || realItemId;

    await updateOccurrenceItemStates(occurrence.id, stableKey, {
      text_value: textValue
    });

    void logTaskActivity({
      taskId: logContext?.taskId ?? taskContext.parentTaskId,
      occurrenceDate: taskContext.occurrenceDate,
      actionType: 'text_input_update',
      newValue: { text_value: textValue },
      metadata: logContext?.fieldTitle ? { field_title: logContext.fieldTitle } : null,
    });
  } else {
    // For direct tasks, update assigned_task_items directly
    const { error } = await supabase
      .from('assigned_task_items')
      .update({ text_value: textValue })
      .eq('id', realItemId);

    if (error) {
      throw error;
    }

    if (logContext?.taskId) {
      void logTaskActivity({
        taskId: logContext.taskId,
        actionType: 'text_input_update',
        newValue: { text_value: textValue },
        metadata: logContext?.fieldTitle ? { field_title: logContext.fieldTitle } : null,
      });
    }
  }
}

/**
 * Fire-and-forget: insert KPI entries for numeric items after task completion
 */
async function insertKpiEntries(params: {
  items: AssignedTaskItem[];
  siteId: string;
  templateId: string;
  occurrenceDate: string;
  recordedBy: string | null;
  sourceTaskId: string;
  sourceOccurrenceId?: string | null;
}): Promise<void> {
  const { items, siteId, templateId, occurrenceDate, recordedBy, sourceTaskId, sourceOccurrenceId } = params;

  const kpiItems = items.filter(item => {
    if (item.text_input_type !== 'number') return false;
    if (!item.kpi_definition_id) return false;
    if (!item.text_value || item.text_value.trim() === '') return false;
    const val = parseFloat(item.text_value.trim());
    return !isNaN(val);
  });

  if (kpiItems.length === 0) return;

  const entries = kpiItems.map(item => {
    const realItemId = item.id.replace('_virtual', '');
    return {
      kpi_definition_id: item.kpi_definition_id!,
      numeric_value: parseFloat(item.text_value!.trim()),
      site_id: siteId,
      template_id: templateId,
      occurrence_date: occurrenceDate,
      recorded_by: recordedBy,
      source_task_id: sourceTaskId,
      source_occurrence_id: sourceOccurrenceId ?? null,
      source_item_id: realItemId
    };
  });

  const { error: upsertError } = await supabase
    .from('kpi_entries')
    .upsert(entries, { onConflict: 'source_task_id,source_item_id,occurrence_date', ignoreDuplicates: true });

  if (upsertError) {
    console.error('[insertKpiEntries] upsert failed', {
      error: upsertError,
      source_task_id: sourceTaskId,
      source_occurrence_id: sourceOccurrenceId,
      occurrence_date: occurrenceDate,
      entry_count: entries.length
    });
  }
}

/**
 * Fire-and-forget: remove KPI entries linked to a specific occurrence
 */
async function deleteKpiEntriesForOccurrence(sourceOccurrenceId: string): Promise<void> {
  const { error } = await supabase
    .from('kpi_entries')
    .delete()
    .eq('source_occurrence_id', sourceOccurrenceId);
  if (error) {
    console.error('[deleteKpiEntriesForOccurrence] failed', { error, sourceOccurrenceId });
  }
}

/**
 * Fire-and-forget: remove KPI entries for a direct (non-occurrence-backed) task
 */
async function deleteKpiEntriesForDirectTask(sourceTaskId: string): Promise<void> {
  const { error } = await supabase
    .from('kpi_entries')
    .delete()
    .eq('source_task_id', sourceTaskId)
    .is('source_occurrence_id', null);
  if (error) {
    console.error('[deleteKpiEntriesForDirectTask] failed', { error, sourceTaskId });
  }
}

/**
 * Fire-and-forget: remove ALL KPI entries for a recurring task (all occurrences)
 */
async function deleteAllKpiEntriesForRecurringTask(sourceTaskId: string): Promise<void> {
  const { error } = await supabase
    .from('kpi_entries')
    .delete()
    .eq('source_task_id', sourceTaskId);
  if (error) {
    console.error('[deleteAllKpiEntriesForRecurringTask] failed', { error, sourceTaskId });
  }
}

/**
 * Complete a task (mark as completed)
 * UPDATED: Only validates required text fields, automatically marks all items as completed
 * UPDATED: Handles both direct tasks and virtual occurrences
 * UPDATED: Added temporal access validation
 */
export async function completeTask(
  taskId: string,
  items: AssignedTaskItem[],
  taskContext?: { isVirtual: boolean; parentTaskId?: string; occurrenceDate?: string }
): Promise<void> {
  if (taskContext?.isVirtual && taskContext.occurrenceDate) {
    assertTaskCanBeModified(taskContext.occurrenceDate, 'completeTask');
  } else {
    const { data: task } = await supabase
      .from('assigned_tasks')
      .select('assigned_date')
      .eq('id', taskId)
      .single();

    if (task) {
      assertTaskCanBeModified(task.assigned_date, 'completeTask');
    }
  }

  addBreadcrumb('task', 'Completion de mission', { taskId, isVirtual: taskContext?.isVirtual });

  const summary = calculateExecutionSummary(items);

  if (!summary.canComplete) {
    throw new Error(`${summary.missingRequiredFields} champ(s) obligatoire(s) non rempli(s)`);
  }

  const now = new Date().toISOString();

  const { data: { session } } = await supabase.auth.getSession();
  const currentUserId = session?.user?.id ?? null;

  if (taskContext?.isVirtual && taskContext.parentTaskId && taskContext.occurrenceDate) {
    const occurrence = await getOrCreateOccurrence(
      taskContext.parentTaskId,
      taskContext.occurrenceDate
    );

    const { data: parentTask } = await supabase
      .from('assigned_tasks')
      .select('template_name, template_description, template_estimated_duration_minutes, template_service_id, site_id, template_id')
      .eq('id', taskContext.parentTaskId)
      .maybeSingle();

    const itemStates: Record<string, any> = {};
    const frozenItems = items.map(item => {
      const realItemId = item.id.replace('_virtual', '');
      const stableKey = item.task_item_id || realItemId;
      itemStates[stableKey] = {
        is_completed: true,
        text_value: item.text_value || null
      };
      return {
        id: realItemId,
        task_item_id: stableKey,
        title: item.title,
        description: item.description,
        order_index: item.order_index,
        requires_text_input: item.requires_text_input,
        is_text_input_required: item.is_text_input_required,
        text_input_placeholder: item.text_input_placeholder ?? null,
        text_input_type: item.text_input_type ?? 'text',
        kpi_definition_id: item.kpi_definition_id ?? null
      };
    });

    const { error } = await supabase
      .from('assigned_task_occurrences')
      .update({
        status: 'completed',
        notes: JSON.stringify({ itemStates, frozenItems }),
        template_name: parentTask?.template_name ?? null,
        template_description: parentTask?.template_description ?? null,
        template_estimated_duration_minutes: parentTask?.template_estimated_duration_minutes ?? null,
        template_service_id: parentTask?.template_service_id ?? null
      })
      .eq('id', occurrence.id);

    if (error) {
      throw new Error(error.message);
    }

    if (parentTask?.site_id && parentTask?.template_id) {
      void insertKpiEntries({
        items,
        siteId: parentTask.site_id,
        templateId: parentTask.template_id,
        occurrenceDate: taskContext.occurrenceDate,
        recordedBy: currentUserId,
        sourceTaskId: taskContext.parentTaskId,
        sourceOccurrenceId: occurrence.id
      });
    }

    void logTaskActivity({
      taskId: taskContext.parentTaskId,
      occurrenceDate: taskContext.occurrenceDate,
      actionType: 'completion',
      newValue: { status: 'completed' },
    });
  } else {
    const { data: taskInfo } = await supabase
      .from('assigned_tasks')
      .select('site_id, template_id, assigned_date')
      .eq('id', taskId)
      .maybeSingle();

    const { error: itemsError } = await supabase
      .from('assigned_task_items')
      .update({
        is_completed: true,
        completed_at: now
      })
      .eq('assigned_task_id', taskId);

    if (itemsError) {
      throw new Error('Erreur lors de la complétion des items');
    }

    const { error } = await supabase
      .from('assigned_tasks')
      .update({
        status: 'completed',
        completed_at: now
      })
      .eq('id', taskId);

    if (error) {
      throw new Error(error.message);
    }

    if (taskInfo?.site_id && taskInfo?.template_id && taskInfo?.assigned_date) {
      void insertKpiEntries({
        items,
        siteId: taskInfo.site_id,
        templateId: taskInfo.template_id,
        occurrenceDate: taskInfo.assigned_date,
        recordedBy: currentUserId,
        sourceTaskId: taskId,
        sourceOccurrenceId: null
      });
    }

    void logTaskActivity({
      taskId,
      actionType: 'completion',
      newValue: { status: 'completed' },
    });
  }
}

export async function getTeamTasksForDate(
  managerId: string,
  date: string
): Promise<AssignedTaskWithDetails[]> {
  return fetchAndAssembleTasks(date, date, {
    siteFilter: { type: 'userSites', userId: managerId }
  });
}

/**
 * Get statistics for team tasks
 */
export interface TeamTaskStats {
  totalTasks: number;
  completedTasks: number;
  notStartedTasks: number;
  operatorCount: number;
  siteCount: number;
}

export function calculateTeamStats(tasks: AssignedTaskWithDetails[]): TeamTaskStats {
  const uniqueOperators = new Set(tasks.map(t => t.assigned_to_user_id));
  const uniqueSites = new Set(tasks.map(t => t.site_id));

  return {
    totalTasks: tasks.length,
    completedTasks: tasks.filter(t => t.status === 'completed').length,
    notStartedTasks: tasks.filter(t => t.status === 'not_started').length,
    operatorCount: uniqueOperators.size,
    siteCount: uniqueSites.size
  };
}

/**
 * Advanced types for manager team view
 */

export interface SiteInfo {
  id: string;
  name: string;
}

export interface OperatorStats {
  operatorId: string;
  operatorName: string;
  operatorUsername: string;
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  lateTasks: number;
  completionRate: number;
  tasks: AssignedTaskWithDetails[];
}

export interface SiteStats {
  siteId: string;
  siteName: string;
  totalTasks: number;
  completedTasks: number;
  lateTasks: number;
  operatorCount: number;
  completionRate: number;
}

export interface EnhancedTeamStats {
  overall: {
    totalTasks: number;
    completedTasks: number;
    lateTasks: number;
    completionRate: number;
  };
  bySite: SiteStats[];
  byOperator: OperatorStats[];
}

export interface TaskAtRisk {
  task: AssignedTaskWithDetails;
  reason: 'overdue' | 'not_started_late';
  minutesLate: number;
}

export interface PerformanceHistory {
  date: string;
  totalTasks: number;
  completedTasks: number;
  completionRate: number;
}

export interface OperatorPerformanceHistory {
  operatorId: string;
  operatorName: string;
  history: PerformanceHistory[];
  averageCompletionRate: number;
  totalTasksCompleted: number;
}

/**
 * Get sites for team view based on user role
 * Unified function that handles both manager and admin access
 * @param userId - The user ID (manager or admin)
 * @param role - The user role ('manager', 'admin', or 'director')
 * @returns List of sites accessible to the user
 */
export async function getTeamSites(userId: string, role: string): Promise<SiteInfo[]> {
  if (role === 'admin' || role === 'director') {
    return getAllSites();
  }
  return getManagerSites(userId);
}

/**
 * Get all sites assigned to a manager
 */
export async function getManagerSites(managerId: string): Promise<SiteInfo[]> {
  const { data, error } = await supabase
    .from('user_sites')
    .select('site:sites!inner(id, name, is_archived)')
    .eq('user_id', managerId)
    .eq('site.is_archived', false);

  if (error) {
    throw error;
  }

  if (!data) return [];

  return data.map((item: { site: SiteInfo }) => item.site).filter(Boolean);
}

/**
 * Get all sites (for admin access)
 * ADMIN: Returns all sites without user filtering, excluding archived sites
 */
export async function getAllSites(): Promise<SiteInfo[]> {
  const { data, error } = await supabase
    .from('sites')
    .select('id, name')
    .eq('is_archived', false)
    .order('name', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
}

/**
 * Get team tasks for a specific site and date based on user role
 * Unified function that handles both manager and admin access
 * @param userId - The user ID (manager or admin)
 * @param role - The user role ('manager', 'admin', or 'director')
 * @param siteId - The site ID to fetch tasks for
 * @param date - The date in YYYY-MM-DD format
 * @returns List of tasks for the site on the specified date
 */
export async function getTeamTasks(
  userId: string,
  role: string,
  siteId: string,
  date: string
): Promise<AssignedTaskWithDetails[]> {
  if (role === 'admin' || role === 'director') {
    return getAllTeamTasksForSiteAndDate(siteId, date);
  }
  return getTeamTasksForSiteAndDate(userId, siteId, date);
}

export async function getTeamTasksForSiteAndDate(
  managerId: string,
  siteId: string,
  date: string
): Promise<AssignedTaskWithDetails[]> {
  return fetchAndAssembleTasks(date, date, {
    siteFilter: { type: 'singleSite', siteId },
    accessCheck: { userId: managerId }
  });
}

export async function getAllTeamTasksForSiteAndDate(
  siteId: string,
  date: string
): Promise<AssignedTaskWithDetails[]> {
  return fetchAndAssembleTasks(date, date, {
    siteFilter: { type: 'singleSite', siteId }
  });
}

/**
 * Get all tasks for a site across a date range (admin/director — no user_sites check)
 * Expands recurring task occurrences the same way the daily view does.
 */
export async function getAllTeamTasksForDateRange(
  siteId: string,
  startDate: string,
  endDate: string,
  signal?: AbortSignal
): Promise<AssignedTaskWithDetails[]> {
  return fetchAndAssembleTasks(startDate, endDate, {
    siteFilter: { type: 'singleSite', siteId }
  }, signal);
}

/**
 * Get tasks for a date range (for weekly/monthly planning)
 * Expands recurring task occurrences the same way the daily view does.
 */
export async function getTeamTasksForDateRange(
  managerId: string,
  startDate: string,
  endDate: string,
  siteId?: string,
  signal?: AbortSignal
): Promise<AssignedTaskWithDetails[]> {
  if (siteId) {
    return fetchAndAssembleTasks(startDate, endDate, {
      siteFilter: { type: 'singleSite', siteId },
      accessCheck: { userId: managerId }
    }, signal);
  }
  return fetchAndAssembleTasks(startDate, endDate, {
    siteFilter: { type: 'userSites', userId: managerId }
  }, signal);
}

/**
 * Calculate enhanced statistics with breakdown by site and operator
 */
export function calculateEnhancedTeamStats(tasks: AssignedTaskWithDetails[]): EnhancedTeamStats {
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const lateTasks = tasks.filter(t => isTaskLate(t)).length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const siteMap = new Map<string, AssignedTaskWithDetails[]>();
  const operatorMap = new Map<string, AssignedTaskWithDetails[]>();

  tasks.forEach(task => {
    if (!siteMap.has(task.site_id)) {
      siteMap.set(task.site_id, []);
    }
    siteMap.get(task.site_id)!.push(task);

    if (!operatorMap.has(task.assigned_to_user_id)) {
      operatorMap.set(task.assigned_to_user_id, []);
    }
    operatorMap.get(task.assigned_to_user_id)!.push(task);
  });

  const bySite: SiteStats[] = Array.from(siteMap.entries()).map(([siteId, siteTasks]) => {
    const siteCompleted = siteTasks.filter(t => t.status === 'completed').length;
    const siteLate = siteTasks.filter(t => isTaskLate(t)).length;
    const uniqueOperators = new Set(siteTasks.map(t => t.assigned_to_user_id));

    return {
      siteId,
      siteName: siteTasks[0]?.site?.name || 'Site inconnu',
      totalTasks: siteTasks.length,
      completedTasks: siteCompleted,
      lateTasks: siteLate,
      operatorCount: uniqueOperators.size,
      completionRate: siteTasks.length > 0 ? Math.round((siteCompleted / siteTasks.length) * 100) : 0
    };
  });

  const byOperator: OperatorStats[] = Array.from(operatorMap.entries()).map(([operatorId, operatorTasks]) => {
    const completed = operatorTasks.filter(t => t.status === 'completed').length;
    const pending = operatorTasks.filter(t => t.status === 'not_started').length;
    const late = operatorTasks.filter(t => isTaskLate(t)).length;

    return {
      operatorId,
      operatorName: operatorTasks[0]?.assigned_to_user?.full_name || 'Opérateur inconnu',
      operatorUsername: operatorTasks[0]?.assigned_to_user?.username || '',
      totalTasks: operatorTasks.length,
      completedTasks: completed,
      pendingTasks: pending,
      lateTasks: late,
      completionRate: operatorTasks.length > 0 ? Math.round((completed / operatorTasks.length) * 100) : 0,
      tasks: operatorTasks
    };
  });

  return {
    overall: {
      totalTasks,
      completedTasks,
      lateTasks,
      completionRate
    },
    bySite,
    byOperator
  };
}

/**
 * Check if a task is late (scheduled time has passed but not completed)
 */
function isTaskLate(task: AssignedTaskWithDetails): boolean {
  if (task.status === 'completed') return false;

  if (!task.scheduled_time) return false;

  const now = new Date();
  const taskDate = new Date(task.assigned_date);
  const [hours, minutes] = task.scheduled_time.split(':').map(Number);
  taskDate.setHours(hours, minutes, 0, 0);

  return now > taskDate;
}

/**
 * Identify tasks at risk (overdue or not started when late)
 */
export function identifyTasksAtRisk(tasks: AssignedTaskWithDetails[]): TaskAtRisk[] {
  const riskyTasks: TaskAtRisk[] = [];
  const now = new Date();

  tasks.forEach(task => {
    if (task.status === 'completed') return;

    if (!task.scheduled_time) return;

    const taskDate = new Date(task.assigned_date);
    const [hours, minutes] = task.scheduled_time.split(':').map(Number);
    taskDate.setHours(hours, minutes, 0, 0);

    if (now > taskDate) {
      const minutesLate = Math.floor((now.getTime() - taskDate.getTime()) / (1000 * 60));

      riskyTasks.push({
        task,
        reason: task.status === 'not_started' ? 'not_started_late' : 'overdue',
        minutesLate
      });
    }
  });

  return riskyTasks.sort((a, b) => b.minutesLate - a.minutesLate);
}

/**
 * Get performance history for a date range
 */
export async function getPerformanceHistory(
  managerId: string,
  startDate: string,
  endDate: string,
  siteId?: string
): Promise<PerformanceHistory[]> {
  const tasks = await getTeamTasksForDateRange(managerId, startDate, endDate, siteId);

  const dateMap = new Map<string, AssignedTaskWithDetails[]>();

  tasks.forEach(task => {
    const date = task.assigned_date;
    if (!dateMap.has(date)) {
      dateMap.set(date, []);
    }
    dateMap.get(date)!.push(task);
  });

  const history: PerformanceHistory[] = Array.from(dateMap.entries())
    .map(([date, dateTasks]) => {
      const completed = dateTasks.filter(t => t.status === 'completed').length;
      return {
        date,
        totalTasks: dateTasks.length,
        completedTasks: completed,
        completionRate: dateTasks.length > 0 ? Math.round((completed / dateTasks.length) * 100) : 0
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  return history;
}

/**
 * Get performance history by operator
 */
export async function getOperatorPerformanceHistory(
  managerId: string,
  startDate: string,
  endDate: string,
  siteId?: string
): Promise<OperatorPerformanceHistory[]> {
  const tasks = await getTeamTasksForDateRange(managerId, startDate, endDate, siteId);

  const operatorMap = new Map<string, AssignedTaskWithDetails[]>();

  tasks.forEach(task => {
    const operatorId = task.assigned_to_user_id;
    if (!operatorMap.has(operatorId)) {
      operatorMap.set(operatorId, []);
    }
    operatorMap.get(operatorId)!.push(task);
  });

  const operatorHistories: OperatorPerformanceHistory[] = Array.from(operatorMap.entries())
    .map(([operatorId, operatorTasks]) => {
      const dateMap = new Map<string, AssignedTaskWithDetails[]>();

      operatorTasks.forEach(task => {
        const date = task.assigned_date;
        if (!dateMap.has(date)) {
          dateMap.set(date, []);
        }
        dateMap.get(date)!.push(task);
      });

      const history: PerformanceHistory[] = Array.from(dateMap.entries())
        .map(([date, dateTasks]) => {
          const completed = dateTasks.filter(t => t.status === 'completed').length;
          return {
            date,
            totalTasks: dateTasks.length,
            completedTasks: completed,
            completionRate: dateTasks.length > 0 ? Math.round((completed / dateTasks.length) * 100) : 0
          };
        })
        .sort((a, b) => a.date.localeCompare(b.date));

      const totalCompleted = operatorTasks.filter(t => t.status === 'completed').length;
      const avgCompletionRate = history.length > 0
        ? Math.round(history.reduce((sum, h) => sum + h.completionRate, 0) / history.length)
        : 0;

      return {
        operatorId,
        operatorName: operatorTasks[0]?.assigned_to_user?.full_name || 'Opérateur inconnu',
        history,
        averageCompletionRate: avgCompletionRate,
        totalTasksCompleted: totalCompleted
      };
    });

  return operatorHistories;
}

/**
 * Reschedule task parameters
 */
export interface RescheduleTaskParams {
  taskId: string;
  newScheduledTime: string;
  currentUserId: string;
  isVirtualOccurrence?: boolean;
  parentTaskId?: string;
  occurrenceDate?: string;
}

/**
 * Reschedule a task or task occurrence to a different time
 * Handles direct tasks, virtual occurrences, and recurring tasks
 *
 * @param params - Reschedule parameters
 * @returns void
 */
export async function rescheduleTask(params: RescheduleTaskParams): Promise<void> {
  const {
    taskId,
    newScheduledTime,
    currentUserId,
    isVirtualOccurrence,
    parentTaskId,
    occurrenceDate
  } = params;

  addBreadcrumb('task', 'Replanification de mission', { taskId, newScheduledTime, isVirtualOccurrence });

  // Verify the current user has permission (all roles except operator)
  const { data: currentUser, error: userError } = await supabase
    .from('users')
    .select('role')
    .eq('id', currentUserId)
    .single();

  if (userError || !currentUser) {
    throw new Error('Utilisateur non trouvé');
  }

  if (currentUser.role === 'operator') {
    throw new Error('Les opérateurs ne peuvent pas replanifier des missions');
  }

  // Get the task to verify it exists and check its status
  let taskToCheck: TaskCheckResult;
  let actualTaskId = taskId;

  if (isVirtualOccurrence && parentTaskId) {
    // For virtual occurrences, check the parent task
    actualTaskId = parentTaskId;
    const { data: parentTask, error: parentError } = await supabase
      .from('assigned_tasks')
      .select('*')
      .eq('id', parentTaskId)
      .single();

    if (parentError || !parentTask) {
      throw new Error('Mission parente non trouvée');
    }
    taskToCheck = parentTask;
  } else {
    // For direct tasks
    const { data: task, error: taskError } = await supabase
      .from('assigned_tasks')
      .select('*')
      .eq('id', taskId)
      .single();

    if (taskError || !task) {
      throw new Error('Mission non trouvée');
    }
    taskToCheck = task;
  }

  // Check if task can be rescheduled (not completed)
  if (taskToCheck.status === 'completed') {
    throw new Error('Impossible de replanifier une mission terminée');
  }

  if (taskToCheck.validated_at) {
    throw new Error('Impossible de replanifier une mission validée');
  }

  // Update scheduled time
  if (isVirtualOccurrence && parentTaskId && occurrenceDate) {
    // Get or create occurrence record
    const occurrence = await getOrCreateOccurrence(parentTaskId, occurrenceDate);

    // Update the occurrence with the new scheduled time
    const { error: updateError } = await supabase
      .from('assigned_task_occurrences')
      .update({ scheduled_time_override: newScheduledTime })
      .eq('id', occurrence.id);

    if (updateError) {
      throw new Error('Erreur lors de la replanification de l\'occurrence');
    }

    void logTaskActivity({
      taskId: parentTaskId,
      occurrenceDate,
      actionType: 'reschedule',
      actorId: currentUserId,
      newValue: { scheduled_time: newScheduledTime },
    });
  } else {
    // Reschedule direct task
    const { error: updateError } = await supabase
      .from('assigned_tasks')
      .update({ scheduled_time: newScheduledTime })
      .eq('id', taskId);

    if (updateError) {
      throw new Error('Erreur lors de la replanification de la mission');
    }

    void logTaskActivity({
      taskId,
      actionType: 'reschedule',
      actorId: currentUserId,
      newValue: { scheduled_time: newScheduledTime },
    });
  }
}

/**
 * Reassign task scope options
 */
export type ReassignScope = 'single' | 'all_future' | 'until_date';

export interface ReassignTaskParams {
  taskId: string;
  newUserId: string;
  scope: ReassignScope;
  untilDate?: string;
  currentUserId: string;
  isVirtualOccurrence?: boolean;
  parentTaskId?: string;
  occurrenceDate?: string;
  newScheduledTime?: string;
}

export type DeleteScope = 'single' | 'all_future' | 'until_date';

export interface DeleteTaskParams {
  taskId: string;
  scope: DeleteScope;
  untilDate?: string;
  currentUserId: string;
  isVirtualOccurrence?: boolean;
  parentTaskId?: string;
  occurrenceDate?: string;
}

async function deleteSingleTask(
  taskId: string,
  isVirtualOccurrence: boolean,
  parentTaskId?: string,
  occurrenceDate?: string
): Promise<void> {
  if (isVirtualOccurrence && parentTaskId && occurrenceDate) {
    const occurrence = await getOrCreateOccurrence(parentTaskId, occurrenceDate);
    const { error: updateError } = await supabase
      .from('assigned_task_occurrences')
      .update({ is_cancelled: true })
      .eq('id', occurrence.id);

    if (updateError) {
      throw new Error('Erreur lors de la suppression de l\'occurrence');
    }
    void deleteKpiEntriesForOccurrence(occurrence.id);
    return;
  }

  const { error: itemsError } = await supabase
    .from('assigned_task_items')
    .delete()
    .eq('assigned_task_id', taskId);

  if (itemsError) {
    throw new Error('Erreur lors de la suppression des items de la mission');
  }

  const { error: deleteError } = await supabase
    .from('assigned_tasks')
    .delete()
    .eq('id', taskId);

  if (deleteError) {
    throw new Error('Erreur lors de la suppression de la mission');
  }
  void deleteKpiEntriesForDirectTask(taskId);
}

async function deleteEntireRecurringTask(parentTaskId: string): Promise<void> {
  void deleteAllKpiEntriesForRecurringTask(parentTaskId);

  const { error: itemsError } = await supabase
    .from('assigned_task_items')
    .delete()
    .eq('assigned_task_id', parentTaskId);

  if (itemsError) {
    throw new Error('Erreur lors de la suppression des items de la mission');
  }

  const { error: occurrencesError } = await supabase
    .from('assigned_task_occurrences')
    .delete()
    .eq('assigned_task_id', parentTaskId);

  if (occurrencesError) {
    throw new Error('Erreur lors de la suppression des occurrences');
  }

  const { error: deleteError } = await supabase
    .from('assigned_tasks')
    .delete()
    .eq('id', parentTaskId);

  if (deleteError) {
    throw new Error('Erreur lors de la suppression de la mission');
  }
}

async function deleteAllFutureTasks(parentTaskId: string, occurrenceDate: string): Promise<void> {
  const { data: parentTask, error: parentError } = await supabase
    .from('assigned_tasks')
    .select('recurrence_start_date')
    .eq('id', parentTaskId)
    .single();

  if (parentError || !parentTask) {
    throw new Error('Mission parente non trouvée');
  }

  const occDate = parseDate(occurrenceDate);
  occDate.setDate(occDate.getDate() - 1);
  const endDate = formatDate(occDate);

  if (parentTask.recurrence_start_date && endDate < parentTask.recurrence_start_date) {
    await deleteEntireRecurringTask(parentTaskId);
    return;
  }

  const { error: updateError } = await supabase
    .from('assigned_tasks')
    .update({ recurrence_end_date: endDate })
    .eq('id', parentTaskId);

  if (updateError) {
    throw new Error('Erreur lors de la suppression des occurrences futures');
  }
}

async function deleteTasksUntilDate(
  parentTaskId: string,
  occurrenceDate: string,
  untilDate: string
): Promise<void> {
  const { data: parentTask, error: parentError } = await supabase
    .from('assigned_tasks')
    .select(`
      *,
      recurrence:recurrence_rules!assigned_tasks_recurrence_rule_id_fkey(*)
    `)
    .eq('id', parentTaskId)
    .single();

  if (parentError || !parentTask) {
    throw new Error('Mission parente non trouvée');
  }

  const rule = Array.isArray(parentTask.recurrence) ? parentTask.recurrence[0] : parentTask.recurrence;
  if (!rule) {
    throw new Error('Règle de récurrence non trouvée');
  }

  const startDate = parseDate(occurrenceDate);
  const endDate = parseDate(untilDate);
  const occurrences = await getTaskOccurrences(parentTask, rule, { startDate, endDate });

  for (const occ of occurrences) {
    if (occ.isCancelled) {
      continue;
    }
    const occurrence = await getOrCreateOccurrence(parentTaskId, occ.date);
    await supabase
      .from('assigned_task_occurrences')
      .update({ is_cancelled: true })
      .eq('id', occurrence.id);
    void deleteKpiEntriesForOccurrence(occurrence.id);
  }
}

export async function deleteTask(params: DeleteTaskParams): Promise<void> {
  const {
    taskId,
    scope,
    untilDate,
    currentUserId,
    isVirtualOccurrence,
    parentTaskId,
    occurrenceDate
  } = params;

  addBreadcrumb('task', 'Suppression de mission', { taskId, scope, isVirtualOccurrence });

  const { data: currentUser, error: userError } = await supabase
    .from('users')
    .select('role')
    .eq('id', currentUserId)
    .single();

  if (userError || !currentUser) {
    throw new Error('Utilisateur non trouvé');
  }

  if (!['manager', 'director', 'admin'].includes(currentUser.role)) {
    throw new Error('Seuls les managers, directeurs et admins peuvent supprimer des missions');
  }

  let taskToCheck: TaskCheckResult;
  let actualTaskId = taskId;

  if (isVirtualOccurrence && parentTaskId) {
    actualTaskId = parentTaskId;
    const { data: parentTask, error: parentError } = await supabase
      .from('assigned_tasks')
      .select('*, template:task_templates(name), site:sites(name), assigned_to_user:users!assigned_tasks_assigned_to_user_id_fkey(id, full_name)')
      .eq('id', parentTaskId)
      .single();

    if (parentError || !parentTask) {
      throw new Error('Mission parente non trouvée');
    }
    taskToCheck = parentTask;
  } else {
    const { data: task, error: taskError } = await supabase
      .from('assigned_tasks')
      .select('*, template:task_templates(name), site:sites(name), assigned_to_user:users!assigned_tasks_assigned_to_user_id_fkey(id, full_name)')
      .eq('id', taskId)
      .single();

    if (taskError || !task) {
      throw new Error('Mission non trouvée');
    }
    taskToCheck = task;
  }

  if (taskToCheck.status === 'completed') {
    throw new Error('Impossible de supprimer une mission terminée');
  }

  if (taskToCheck.validated_at) {
    throw new Error('Impossible de supprimer une mission validée');
  }

  const auditLogData = {
    deleted_by: currentUserId,
    task_id: actualTaskId,
    task_name: taskToCheck.template?.name || 'Mission sans nom',
    assigned_to_user_id: taskToCheck.assigned_to_user_id,
    assigned_to_user_name: taskToCheck.assigned_to_user?.full_name || null,
    site_id: taskToCheck.site_id,
    site_name: taskToCheck.site?.name || null,
    deletion_scope: scope,
    occurrence_date: occurrenceDate || null,
    until_date: untilDate || null,
    is_recurring: taskToCheck.is_recurring || false
  };

  if (scope === 'single') {
    await deleteSingleTask(taskId, !!isVirtualOccurrence, parentTaskId, occurrenceDate);
  } else if (scope === 'all_future') {
    if (!isVirtualOccurrence || !parentTaskId || !occurrenceDate) {
      throw new Error('La portée "toutes les futures" nécessite une mission récurrente');
    }
    await deleteAllFutureTasks(parentTaskId, occurrenceDate);
  } else if (scope === 'until_date' && untilDate) {
    if (!isVirtualOccurrence || !parentTaskId || !occurrenceDate) {
      throw new Error('La portée "jusqu\'à" nécessite une mission récurrente');
    }
    if (untilDate <= occurrenceDate) {
      throw new Error('La date de fin doit être postérieure à la date actuelle');
    }
    await deleteTasksUntilDate(parentTaskId, occurrenceDate, untilDate);
  }

  await supabase
    .from('task_deletion_logs')
    .insert(auditLogData);
}

async function reassignSingleTask(
  taskId: string,
  newUserId: string,
  isVirtualOccurrence: boolean,
  parentTaskId?: string,
  occurrenceDate?: string,
  newScheduledTime?: string
): Promise<void> {
  if (isVirtualOccurrence && parentTaskId && occurrenceDate) {
    const occurrence = await getOrCreateOccurrence(parentTaskId, occurrenceDate);
    const occUpdateData: OccurrenceReassignUpdate = { assigned_to_user_id_override: newUserId };
    if (newScheduledTime) {
      occUpdateData.scheduled_time_override = newScheduledTime;
    }

    const { error: updateError } = await supabase
      .from('assigned_task_occurrences')
      .update(occUpdateData)
      .eq('id', occurrence.id);

    if (updateError) {
      throw new Error('Erreur lors de la réattribution de l\'occurrence');
    }
    return;
  }

  const directUpdateData: DirectTaskReassignUpdate = { assigned_to_user_id: newUserId };
  if (newScheduledTime) {
    directUpdateData.scheduled_time = newScheduledTime;
  }

  const { error: updateError } = await supabase
    .from('assigned_tasks')
    .update(directUpdateData)
    .eq('id', taskId);

  if (updateError) {
    throw new Error('Erreur lors de la réattribution de la mission');
  }
}

async function reassignAllFutureTasks(
  parentTaskId: string,
  occurrenceDate: string,
  newUserId: string,
  newScheduledTime?: string
): Promise<void> {
  const ruleUpdate: ReassignmentRuleUpdate = {
    reassignment_from_date: occurrenceDate,
    reassignment_user_id: newUserId,
    reassignment_scheduled_time: newScheduledTime || null
  };

  const { error: updateError } = await supabase
    .from('assigned_tasks')
    .update(ruleUpdate)
    .eq('id', parentTaskId);

  if (updateError) {
    throw new Error('Erreur lors de l\'application de la règle de réattribution');
  }

  const { error: deleteError } = await supabase
    .from('assigned_task_occurrences')
    .delete()
    .eq('assigned_task_id', parentTaskId)
    .gte('occurrence_date', occurrenceDate)
    .not('is_cancelled', 'eq', true);

}

async function reassignTasksUntilDate(
  parentTaskId: string,
  occurrenceDate: string,
  untilDate: string,
  newUserId: string,
  newScheduledTime?: string
): Promise<void> {
  const { data: parentTask, error: parentError } = await supabase
    .from('assigned_tasks')
    .select(`
      *,
      recurrence:recurrence_rules!assigned_tasks_recurrence_rule_id_fkey(*)
    `)
    .eq('id', parentTaskId)
    .single();

  if (parentError || !parentTask) {
    throw new Error('Mission parente non trouvée');
  }

  const rule = Array.isArray(parentTask.recurrence) ? parentTask.recurrence[0] : parentTask.recurrence;
  if (!rule) {
    throw new Error('Règle de récurrence non trouvée');
  }

  const startDate = parseDate(occurrenceDate);
  const endDate = parseDate(untilDate);
  const occurrences = await getTaskOccurrences(parentTask, rule, { startDate, endDate });

  const untilUpdateData: OccurrenceReassignUpdate = { assigned_to_user_id_override: newUserId };
  if (newScheduledTime) {
    untilUpdateData.scheduled_time_override = newScheduledTime;
  }

  for (const occ of occurrences) {
    if (occ.isCancelled) {
      continue;
    }
    const occurrence = await getOrCreateOccurrence(parentTaskId, occ.date);
    await supabase
      .from('assigned_task_occurrences')
      .update(untilUpdateData)
      .eq('id', occurrence.id);
  }
}

export async function reassignTask(params: ReassignTaskParams): Promise<void> {
  const {
    taskId,
    newUserId,
    scope,
    untilDate,
    currentUserId,
    isVirtualOccurrence,
    parentTaskId,
    occurrenceDate,
    newScheduledTime
  } = params;

  addBreadcrumb('task', 'Reassignation de mission', { taskId, newUserAssigned: !!newUserId, scope, isVirtualOccurrence });

  const { data: currentUser, error: userError } = await supabase
    .from('users')
    .select('role')
    .eq('id', currentUserId)
    .single();

  if (userError || !currentUser) {
    throw new Error('Utilisateur non trouvé');
  }

  if (currentUser.role === 'operator') {
    throw new Error('Les opérateurs ne peuvent pas réattribuer des missions');
  }

  const { data: newUser, error: newUserError } = await supabase
    .from('users')
    .select('id')
    .eq('id', newUserId)
    .single();

  if (newUserError || !newUser) {
    throw new Error('Nouvel utilisateur non trouvé');
  }

  let taskToCheck: TaskCheckResult;

  if (isVirtualOccurrence && parentTaskId) {
    const { data: parentTask, error: parentError } = await supabase
      .from('assigned_tasks')
      .select('*, site:sites(id)')
      .eq('id', parentTaskId)
      .single();

    if (parentError || !parentTask) {
      throw new Error('Mission parente non trouvée');
    }
    taskToCheck = parentTask;
  } else {
    const { data: task, error: taskError } = await supabase
      .from('assigned_tasks')
      .select('*, site:sites(id)')
      .eq('id', taskId)
      .single();

    if (taskError || !task) {
      throw new Error('Mission non trouvée');
    }
    taskToCheck = task;
  }

  const { data: newUserSites, error: newUserSitesError } = await supabase
    .from('user_sites')
    .select('site_id')
    .eq('user_id', newUserId);

  if (newUserSitesError || !newUserSites || newUserSites.length === 0) {
    throw new Error('Le nouvel utilisateur n\'est assigné à aucun site');
  }

  if (!newUserSites.map(us => us.site_id).includes(taskToCheck.site_id)) {
    throw new Error('Le nouvel utilisateur n\'appartient pas au même site que la mission');
  }

  if (taskToCheck.status === 'completed') {
    throw new Error('Impossible de réattribuer une mission terminée');
  }

  if (taskToCheck.validated_at) {
    throw new Error('Impossible de réattribuer une mission validée');
  }

  if (scope === 'single') {
    await reassignSingleTask(taskId, newUserId, !!isVirtualOccurrence, parentTaskId, occurrenceDate, newScheduledTime);
  } else if (scope === 'all_future') {
    if (!isVirtualOccurrence || !parentTaskId || !occurrenceDate) {
      throw new Error('La portée "toutes les futures" nécessite une mission récurrente');
    }
    await reassignAllFutureTasks(parentTaskId, occurrenceDate, newUserId, newScheduledTime);
  } else if (scope === 'until_date' && untilDate) {
    if (!isVirtualOccurrence || !parentTaskId || !occurrenceDate) {
      throw new Error('La portée "jusqu\'à" nécessite une mission récurrente');
    }
    if (untilDate <= occurrenceDate) {
      throw new Error('La date de fin doit être postérieure à la date actuelle');
    }
    await reassignTasksUntilDate(parentTaskId, occurrenceDate, untilDate, newUserId, newScheduledTime);
  }

  void logTaskActivity({
    taskId: (isVirtualOccurrence && parentTaskId) ? parentTaskId : taskId,
    occurrenceDate: occurrenceDate ?? null,
    actionType: 'reassignment',
    actorId: currentUserId,
    newValue: { assigned_to_user_id: newUserId, scope },
    metadata: newScheduledTime ? { new_scheduled_time: newScheduledTime } : null,
  });
}

/**
 * Update task status (for admin inline editing)
 * Allows admins to change the status without temporal restrictions
 * @param taskId - The task ID (or virtual occurrence ID)
 * @param newStatus - The new status to set
 * @param taskContext - Context for virtual occurrences
 * @param isAdmin - Whether the user is an admin (bypasses temporal checks)
 */
export async function updateTaskStatus(
  taskId: string,
  newStatus: 'not_started' | 'completed' | 'overdue',
  taskContext?: { isVirtual: boolean; parentTaskId?: string; occurrenceDate?: string },
  isAdmin: boolean = false
): Promise<void> {
  if (!isAdmin) {
    if (taskContext?.isVirtual && taskContext.occurrenceDate) {
      assertTaskCanBeModified(taskContext.occurrenceDate, 'updateTaskStatus');
    } else {
      const { data: task } = await supabase
        .from('assigned_tasks')
        .select('assigned_date')
        .eq('id', taskId)
        .single();

      if (task) {
        assertTaskCanBeModified(task.assigned_date, 'updateTaskStatus');
      }
    }
  }

  const now = new Date().toISOString();
  const statusUpdate: TaskStatusUpdate = {
    status: newStatus,
    completed_at: newStatus === 'completed' ? now : null
  };

  if (taskContext?.isVirtual && taskContext.parentTaskId && taskContext.occurrenceDate) {
    const occurrence = await getOrCreateOccurrence(
      taskContext.parentTaskId,
      taskContext.occurrenceDate
    );

    if (newStatus === 'completed') {
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id ?? null;

      const [itemsResult, occNotesResult, parentTaskResult] = await Promise.all([
        supabase
          .from('assigned_task_items')
          .select('id, task_item_id, title, description, order_index, requires_text_input, is_text_input_required, text_input_placeholder, text_input_type, kpi_definition_id')
          .eq('assigned_task_id', taskContext.parentTaskId)
          .order('order_index'),
        supabase
          .from('assigned_task_occurrences')
          .select('notes')
          .eq('id', occurrence.id)
          .maybeSingle(),
        supabase
          .from('assigned_tasks')
          .select('template_name, template_description, template_estimated_duration_minutes, template_service_id, site_id, template_id')
          .eq('id', taskContext.parentTaskId)
          .maybeSingle()
      ]);

      if (itemsResult.error) {
        throw new Error(`Erreur lors de la récupération des items: ${itemsResult.error.message}`);
      }
      if (occNotesResult.error) {
        throw new Error(`Erreur lors de la récupération des notes: ${occNotesResult.error.message}`);
      }
      if (parentTaskResult.error) {
        throw new Error(`Erreur lors de la récupération de la tâche parente: ${parentTaskResult.error.message}`);
      }

      let parsedNotes: Record<string, any> = {};
      let itemStates: Record<string, any> = {};
      if (occNotesResult.data?.notes) {
        try {
          parsedNotes = JSON.parse(occNotesResult.data.notes);
          itemStates = parsedNotes.itemStates || {};
        } catch (e) {
          console.error('[updateTaskStatus] Failed to parse occurrence notes', {
            error: (e as Error).message,
            occurrenceId: occurrence.id,
            taskId: taskContext.parentTaskId,
          });
        }
      }

      const parentItems = itemsResult.data || [];
      const frozenItems = parentItems.map(item => ({
        id: item.id,
        task_item_id: item.task_item_id || item.id,
        title: item.title,
        description: item.description,
        order_index: item.order_index,
        requires_text_input: item.requires_text_input,
        is_text_input_required: item.is_text_input_required,
        text_input_placeholder: item.text_input_placeholder ?? null,
        text_input_type: item.text_input_type ?? 'text',
        kpi_definition_id: item.kpi_definition_id ?? null
      }));

      const completedItemStates: Record<string, any> = {};
      for (const item of parentItems) {
        const stableKey = item.task_item_id || item.id;
        const existing = itemStates[stableKey] || itemStates[item.id] || {};
        completedItemStates[stableKey] = { ...existing, is_completed: true };
      }

      const parentTask = parentTaskResult.data;
      const { error } = await supabase
        .from('assigned_task_occurrences')
        .update({
          ...statusUpdate,
          notes: JSON.stringify({ ...parsedNotes, itemStates: completedItemStates, frozenItems }),
          template_name: parentTask?.template_name ?? null,
          template_description: parentTask?.template_description ?? null,
          template_estimated_duration_minutes: parentTask?.template_estimated_duration_minutes ?? null,
          template_service_id: parentTask?.template_service_id ?? null
        })
        .eq('id', occurrence.id);

      if (error) {
        throw new Error(error.message);
      }

      if (parentTask?.site_id && parentTask?.template_id) {
        const itemsForKpi: AssignedTaskItem[] = parentItems.map(item => {
          const stableKey = item.task_item_id || item.id;
          return {
            id: item.id,
            assigned_task_id: taskContext.parentTaskId!,
            task_item_id: stableKey,
            title: item.title,
            description: item.description,
            order_index: item.order_index,
            is_completed: true,
            completed_at: now,
            requires_text_input: item.requires_text_input,
            is_text_input_required: item.is_text_input_required,
            text_input_placeholder: item.text_input_placeholder ?? null,
            text_input_type: (item.text_input_type ?? 'text') as 'text' | 'number',
            kpi_definition_id: item.kpi_definition_id ?? null,
            text_value: (itemStates[stableKey] || itemStates[item.id])?.text_value ?? null
          };
        });
        void insertKpiEntries({
          items: itemsForKpi,
          siteId: parentTask.site_id,
          templateId: parentTask.template_id,
          occurrenceDate: taskContext.occurrenceDate,
          recordedBy: currentUserId,
          sourceTaskId: taskContext.parentTaskId,
          sourceOccurrenceId: occurrence.id
        });
      }
    } else {
      const revertNotesResult = await supabase
        .from('assigned_task_occurrences')
        .select('notes')
        .eq('id', occurrence.id)
        .maybeSingle();

      if (revertNotesResult.error) {
        throw new Error(`Erreur lors de la récupération des notes: ${revertNotesResult.error.message}`);
      }

      let revertParsedNotes: Record<string, any> = {};
      let revertItemStates: Record<string, any> = {};
      if (revertNotesResult.data?.notes) {
        try {
          revertParsedNotes = JSON.parse(revertNotesResult.data.notes);
          revertItemStates = revertParsedNotes.itemStates || {};
        } catch (e) {
          console.error('[updateTaskStatus] Failed to parse revert occurrence notes', {
            error: (e as Error).message,
            occurrenceId: occurrence.id,
          });
        }
      }

      const resetItemStates: Record<string, any> = {};
      for (const id of Object.keys(revertItemStates)) {
        resetItemStates[id] = { ...revertItemStates[id], is_completed: false };
      }

      const { error } = await supabase
        .from('assigned_task_occurrences')
        .update({
          ...statusUpdate,
          notes: JSON.stringify({ ...revertParsedNotes, itemStates: resetItemStates }),
        })
        .eq('id', occurrence.id);

      if (error) {
        throw new Error(error.message);
      }

      void deleteKpiEntriesForOccurrence(occurrence.id);
    }

    void logTaskActivity({
      taskId: taskContext.parentTaskId,
      occurrenceDate: taskContext.occurrenceDate,
      actionType: 'status_change',
      newValue: { status: newStatus },
    });
  } else {
    if (newStatus === 'completed') {
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id ?? null;

      const [taskInfoResult, itemsResult] = await Promise.all([
        supabase
          .from('assigned_tasks')
          .select('site_id, template_id, assigned_date')
          .eq('id', taskId)
          .maybeSingle(),
        supabase
          .from('assigned_task_items')
          .select('id, title, description, order_index, requires_text_input, is_text_input_required, text_input_placeholder, text_input_type, kpi_definition_id, text_value, task_item_id')
          .eq('assigned_task_id', taskId)
      ]);

      if (taskInfoResult.error) {
        throw new Error(`Erreur lors de la récupération des informations de la tâche: ${taskInfoResult.error.message}`);
      }
      if (itemsResult.error) {
        throw new Error(`Erreur lors de la récupération des items: ${itemsResult.error.message}`);
      }

      const { error: itemsUpdateError } = await supabase
        .from('assigned_task_items')
        .update({ is_completed: true, completed_at: now })
        .eq('assigned_task_id', taskId);

      if (itemsUpdateError) {
        throw new Error('Erreur lors de la complétion des items');
      }

      const { error } = await supabase
        .from('assigned_tasks')
        .update(statusUpdate)
        .eq('id', taskId);

      if (error) {
        throw new Error(error.message);
      }

      const taskInfo = taskInfoResult.data;
      if (taskInfo?.site_id && taskInfo?.template_id && taskInfo?.assigned_date && itemsResult.data) {
        const itemsForKpi: AssignedTaskItem[] = itemsResult.data.map(item => ({
          id: item.id,
          assigned_task_id: taskId,
          task_item_id: item.task_item_id,
          title: item.title,
          description: item.description,
          order_index: item.order_index,
          is_completed: true,
          completed_at: now,
          requires_text_input: item.requires_text_input,
          is_text_input_required: item.is_text_input_required,
          text_input_placeholder: item.text_input_placeholder ?? null,
          text_input_type: (item.text_input_type ?? 'text') as 'text' | 'number',
          kpi_definition_id: item.kpi_definition_id ?? null,
          text_value: item.text_value ?? null
        }));
        void insertKpiEntries({
          items: itemsForKpi,
          siteId: taskInfo.site_id,
          templateId: taskInfo.template_id,
          occurrenceDate: taskInfo.assigned_date,
          recordedBy: currentUserId,
          sourceTaskId: taskId,
          sourceOccurrenceId: null
        });
      }
    } else {
      const { error: itemsResetError } = await supabase
        .from('assigned_task_items')
        .update({ is_completed: false, completed_at: null })
        .eq('assigned_task_id', taskId);

      if (itemsResetError) {
        throw new Error(`Erreur lors de la réinitialisation des items: ${itemsResetError.message}`);
      }

      const { error } = await supabase
        .from('assigned_tasks')
        .update(statusUpdate)
        .eq('id', taskId);

      if (error) {
        throw new Error(error.message);
      }

      void deleteKpiEntriesForDirectTask(taskId);
    }

    void logTaskActivity({
      taskId,
      actionType: 'status_change',
      newValue: { status: newStatus },
    });
  }
}

/**
 * Fire-and-forget: upsert a KPI entry after an admin edits a numeric field on a completed task.
 * No-ops silently if the item is not KPI-backed, value is non-numeric, or the task is not completed.
 */
async function tryUpsertKpiEntryForAdminEdit(
  realItemId: string,
  textValue: string,
  isVirtual: boolean,
  parentTaskId: string | undefined,
  occurrenceDate: string | undefined
): Promise<void> {
  const { data: item } = await supabase
    .from('assigned_task_items')
    .select('kpi_definition_id, text_input_type, assigned_task_id')
    .eq('id', realItemId)
    .maybeSingle();

  if (!item?.kpi_definition_id || item.text_input_type !== 'number') return;
  const numeric = parseFloat(textValue.trim());
  if (isNaN(numeric)) return;

  let siteId: string | null = null;
  let templateId: string | null = null;
  let entryOccurrenceDate: string | null = null;
  let sourceOccurrenceId: string | null = null;
  let sourceTaskId: string | null = null;

  if (isVirtual && parentTaskId && occurrenceDate) {
    const [taskResult, occResult] = await Promise.all([
      supabase
        .from('assigned_tasks')
        .select('site_id, template_id')
        .eq('id', parentTaskId)
        .maybeSingle(),
      supabase
        .from('assigned_task_occurrences')
        .select('id, status')
        .eq('assigned_task_id', parentTaskId)
        .eq('occurrence_date', occurrenceDate)
        .maybeSingle()
    ]);

    if (!occResult.data || occResult.data.status !== 'completed') return;
    if (!taskResult.data) return;

    siteId = taskResult.data.site_id;
    templateId = taskResult.data.template_id;
    entryOccurrenceDate = occurrenceDate;
    sourceOccurrenceId = occResult.data.id;
    sourceTaskId = parentTaskId;
  } else {
    const { data: task } = await supabase
      .from('assigned_tasks')
      .select('site_id, template_id, assigned_date, status')
      .eq('id', item.assigned_task_id)
      .maybeSingle();

    if (!task || task.status !== 'completed') return;

    siteId = task.site_id;
    templateId = task.template_id;
    entryOccurrenceDate = task.assigned_date;
    sourceTaskId = item.assigned_task_id;
  }

  if (!siteId || !templateId || !entryOccurrenceDate || !sourceTaskId) return;

  const { data: { session } } = await supabase.auth.getSession();
  const recordedBy = session?.user?.id ?? null;

  const { error } = await supabase
    .from('kpi_entries')
    .upsert({
      kpi_definition_id: item.kpi_definition_id,
      numeric_value: numeric,
      site_id: siteId,
      template_id: templateId,
      occurrence_date: entryOccurrenceDate,
      recorded_by: recordedBy,
      source_task_id: sourceTaskId,
      source_occurrence_id: sourceOccurrenceId ?? null,
      source_item_id: realItemId
    }, { onConflict: 'source_task_id,source_item_id,occurrence_date' });

  if (error) {
    console.error('[tryUpsertKpiEntryForAdminEdit] upsert failed', { error, realItemId });
  }
}

/**
 * Update task item text input value (admin version without temporal restrictions)
 * @param itemId - The item ID
 * @param textValue - The text value to set
 * @param taskContext - Context for virtual occurrences
 */
export async function updateTaskItemTextInputAsAdmin(
  itemId: string,
  textValue: string,
  taskContext?: { isVirtual: boolean; parentTaskId?: string; occurrenceDate?: string },
  logContext?: { taskId?: string; fieldTitle?: string }
): Promise<void> {
  const realItemId = itemId.replace('_virtual', '');

  if (taskContext?.isVirtual && taskContext.parentTaskId && taskContext.occurrenceDate) {
    const occurrence = await getOrCreateOccurrence(
      taskContext.parentTaskId,
      taskContext.occurrenceDate
    );

    const { data: itemRow } = await supabase
      .from('assigned_task_items')
      .select('task_item_id')
      .eq('id', realItemId)
      .maybeSingle();
    const stableKey = itemRow?.task_item_id || realItemId;

    await updateOccurrenceItemStates(occurrence.id, stableKey, {
      text_value: textValue
    });

    void tryUpsertKpiEntryForAdminEdit(
      realItemId,
      textValue,
      true,
      taskContext.parentTaskId,
      taskContext.occurrenceDate
    );

    void logTaskActivity({
      taskId: logContext?.taskId ?? taskContext.parentTaskId,
      occurrenceDate: taskContext.occurrenceDate,
      actionType: 'text_input_update',
      newValue: { text_value: textValue },
      metadata: logContext?.fieldTitle ? { field_title: logContext.fieldTitle } : null,
    });
  } else {
    const { error } = await supabase
      .from('assigned_task_items')
      .update({ text_value: textValue })
      .eq('id', realItemId);

    if (error) {
      throw new Error(error.message);
    }

    void tryUpsertKpiEntryForAdminEdit(realItemId, textValue, false, undefined, undefined);

    if (logContext?.taskId) {
      void logTaskActivity({
        taskId: logContext.taskId,
        actionType: 'text_input_update',
        newValue: { text_value: textValue },
        metadata: logContext?.fieldTitle ? { field_title: logContext.fieldTitle } : null,
      });
    }
  }
}

